import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import AppPaths from '../../../AppPaths';
import ImageGallery from '../../../components/ImageGallery/ImageGallery';
import * as events from '../../../lib/analytics/events';
import { cartActions as storeCartActions } from '../../../reducers/storeCart';
import AddToBagButton from '../components/AddToBagButton';
import ProductTitle from '../components/ProductTitle';
import styles from '../ProductDetails.module.scss';
import { useLocale } from '../../../contexts/LocaleContext';

interface MiniKitDetailsProps {
  productId: string;
  priceInCents: number;
  sku: string;
}

const COLOR_KIT_PRODUCT_ID = 'mini-attachment-color';

/**
 * PDP for the sellable Mini silicone covers — Black Kit (`mini-attachment`) and Color Kit
 * (`mini-attachment-color`). Both are simple single-SKU products, so this mirrors
 * ChargingBaseDetails. The shop-facing title comes from i18n (not the config `name`, which stays
 * "Fi Mini Attachment Pack" for internal RMA/return consistency).
 */
export default function MiniKitDetails({ productId, priceInCents, sku }: MiniKitDetailsProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { currency } = useLocale();

  const isColorKit = productId === COLOR_KIT_PRODUCT_ID;
  const productName = isColorKit ? t('mini_color_kit') : t('mini_black_kit');
  // The Color Pack ships five covers, so it gets its own copy instead of the shared kit blurb.
  const description = isColorKit ? t('mini_color_kit_description') : t('mini_kit_description');

  useEffect(() => {
    events.miniKitViewed({ priceInCents, productName, sku, currency });
  }, [priceInCents, productName, sku, currency]);

  const onAddToBag = useCallback(() => {
    dispatch(storeCartActions.addSingleLineCartItem({ sku, currency }));

    events.miniKitAddedToBag({ priceInCents, productName, sku, currency });

    history.push(AppPaths.Bag);

    return true;
  }, [dispatch, history, priceInCents, productName, sku, currency]);

  return (
    <>
      <div className={styles.productContainer}>
        <ImageGallery
          media={[
            {
              type: 'image',
              url: `/product_images/${productId}.jpg`,
              retinaWidth: 1080,
              retinaHeight: 720,
            },
          ]}
        />
        <div className={styles.productDetails}>
          <div className={styles.productDetailsHeader}>
            <div className={styles.productDetailsTitle}>
              <ProductTitle productName={productName} price={{ priceInCents }} preserveNameCase />
            </div>
            <p className={styles.description}>{description}</p>
          </div>

          <div className={styles.productAction}>
            <AddToBagButton onAddToBag={onAddToBag} />
          </div>
        </div>
      </div>
    </>
  );
}
